// Component: tradeLevelUtils.js
// Purpose: Trade span mapping and level display configuration for chart visualization

import { toUnixSec } from './tradeMarkerBuilder';

/*>>> DEFAULTS <<<*/
const LEVEL_DISPLAY_DEFAULTS = {
    enabled: true,
    mode: 'price_lines', // 'price_lines' | 'band' | 'scoped_lines'
    hover_only: true,
    lines: {
        entry: { color: '#FFFFFF', line_width: 2, line_style: 'solid' },
        sl:    { color: '#EF4444', line_width: 2, line_style: 'dashed' },
        tp:    { color: '#10B981', line_width: 2, line_style: 'dashed' },
    },
    band_fill: 'rgba(255, 255, 255, 0.05)',
};

/**
 * Build a sorted array of trade spans for binary search on crosshair move.
 * Each entry: { entryTime, exitTime, trade, levelHistoryByTime }
 * Memory: O(trades), not O(total_bars).
 */
export const buildTradeSpanMap = (trades) => {
    if (!trades || trades.length === 0) return [];

    //--> Timestamp coercion via shared canonical helper (tradeMarkerBuilder.toUnixSec)

    return trades
        .filter((t) => t.level_history && t.level_history.length > 0)
        .map((trade) => {
            //--> Build time->context lookup for this trade's level_history
            const levelHistoryByTime = new Map();
            for (const entry of trade.level_history) {
                const time = toUnixSec(entry.timestamp);
                if (time != null) levelHistoryByTime.set(time, entry);
            }

            return {
                entryTime: toUnixSec(trade.entry_time ?? trade.entryTime),
                exitTime: toUnixSec(trade.exit_time ?? trade.exitTime) ?? Infinity,
                trade,
                levelHistoryByTime,
            };
        })
        .filter((span) => span.entryTime != null)
        .sort((a, b) => a.entryTime - b.entryTime);
};

/**
 * Binary search for the trade enclosing a given timestamp.
 * Returns { trade, context } or null.
 */
export const findTradeAtTime = (spanMap, time) => {
    if (!spanMap || spanMap.length === 0) return null;

    //--> Binary search for enclosing trade span
    let lo = 0;
    let hi = spanMap.length - 1;

    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        const span = spanMap[mid];

        if (time < span.entryTime) {
            hi = mid - 1;
        } else if (time > span.exitTime) {
            lo = mid + 1;
        } else {
            //--> time is within this trade's span, find closest context
            const context = span.levelHistoryByTime.get(time);
            if (context) {
                return { trade: span.trade, context };
            }
            //--> Fallback: find nearest context by scanning level_history
            let closest = null;
            let minDist = Infinity;
            for (const [t, ctx] of span.levelHistoryByTime) {
                const dist = Math.abs(t - time);
                if (dist < minDist) {
                    minDist = dist;
                    closest = ctx;
                }
            }
            return closest ? { trade: span.trade, context: closest } : null;
        }
    }

    return null;
};

/**
 * Resolve level_display config for a node, merging with defaults.
 */
export const getLevelDisplayConfig = (node) => {
    const custom = node?.data?.visual_settings?.level_display || {};
    return {
        ...LEVEL_DISPLAY_DEFAULTS,
        ...custom,
        lines: {
            entry: { ...LEVEL_DISPLAY_DEFAULTS.lines.entry, ...(custom.lines?.entry || {}) },
            sl:    { ...LEVEL_DISPLAY_DEFAULTS.lines.sl,    ...(custom.lines?.sl || {}) },
            tp:    { ...LEVEL_DISPLAY_DEFAULTS.lines.tp,    ...(custom.lines?.tp || {}) },
        },
    };
};

export { LEVEL_DISPLAY_DEFAULTS };

//--> Shape per-bar strategy:trade_context snapshots into the levelHistory the chart expects.
//*   Splits snapshots by position.entry_time so a buffer that accumulated across multiple
//*   open→close→reopen cycles for the same SII (close-then-reopen race against
//*   syncLevelHistorySubscriptions' unsubscribe-then-resubscribe cycle) renders as
//*   distinct trade segments instead of one band stretching across the gap. Earlier
//*   segments close at their last known snapshot.
//*
//*   The final segment's openness depends on `isPositionOpen`:
//*     - true  → exit_time=null so the band projects forward to the live bar
//*     - false → exit_time=last snapshot timestamp so the band stops at the actual exit
//*
//*   Without the flag, after a position closes the WS topic stops publishing but the
//*   buffer is preserved for hover; treating the buffer as still-open made the band
//*   project forward indefinitely until the next entry reset the buffer.
export const buildLiveLevelHistory = (snapshots, { isPositionOpen = true } = {}) => {
    if (!snapshots || snapshots.length === 0) return null;

    //--> Group consecutive snapshots by position.entry_time and side. A new entry_time or side means a new trade.
    //*   Using side prevents flip-exit trades (same immediate entry_time but different side) from merging.
    //! Grouping STAYS on entry_time (NOT the coalesced identityKey): early PRE-FILL snapshots carry a
    //!  null fill_bar_start (promoted only after the fill republish, below), so keying the grouping on it
    //!  would split/merge on a field null for the first N snapshots. Only the CROSS-source MERGE dedup is
    //!  re-keyed to fill_bar_start ?? entry_time (see identityKey / buildCombinedLevelHistory) — issue #15.
    const groups = [];
    let currentKey = null;
    let currentGroup = null;
    for (const snap of snapshots) {
        const side = snap?.position?.side ? String(snap.position.side).toLowerCase() : null;
        const key = snap?.position?.entry_time ? `${snap.position.entry_time}_${side}` : null;
        if (key !== currentKey) {
            currentKey = key;
            currentGroup = { entry_time: snap?.position?.entry_time, side: snap?.position?.side, history: [] };
            groups.push(currentGroup);
        }
        currentGroup.history.push(snap);
    }

    return groups.map((g, idx) => {
        const isLast = idx === groups.length - 1;
        //--> Use MAX timestamp instead of array-tail timestamp.
        //*   2026-04-27: WS publish path can interleave a per-bar snapshot
        //*   AFTER the exit-bar snapshot in arrival order even though its
        //*   timestamp is earlier (per-bar publish at bar.bar_start runs at
        //*   bar close in sim time, exit-bar publish at fill instant — wall-
        //*   clock arrival order differs from timestamp order under sim
        //*   replay). With array-tail, the band's right edge would roll
        //*   BACK to the per-bar's timestamp, ending the band one bar
        //*   before the actual exit candle. Reducing over the group to find
        //*   the maximum timestamp is robust to arrival-order races.
        let lastTs = null;
        for (const snap of g.history) {
            const t = snap?.timestamp;
            if (!t) continue;
            if (lastTs == null || new Date(t).getTime() > new Date(lastTs).getTime()) {
                lastTs = t;
            }
        }
        //--> Promote fill_bar_start from the first snapshot that carries it
        //*   to the trade top-level. TradeLevelOverlay.toBandSegment reads
        //*   `trade.fill_bar_start` as the anchor precedence winner so the
        //*   chart band starts on the actual entry candle instead of the
        //*   signal-arrival candle. Backend (_build_trade_context) stamps
        //*   position.fill_bar_start on the snapshot AFTER the fill republish
        //*   lands, so this scan finds the first populated value. Without
        //*   this promotion the field never reaches toBandSegment and the
        //*   preliminary signal-bar point survives the anchor filter,
        //*   producing the one-candle-early band start.
        let fillBarStart = null;
        for (const snap of g.history) {
            const v = snap?.position?.fill_bar_start;
            if (v != null) { fillBarStart = v; break; }
        }
        return {
            entry_time: g.entry_time,
            fill_bar_start: fillBarStart,
            //--> Earlier groups are always closed; final group stays open only while position is open.
            exit_time: isLast && isPositionOpen ? null : lastTs,
            side: g.side,
            level_history: g.history,
        };
    });
};

//--> Derive "is the last live segment still open?" from the position snapshot
//*   + the WS snapshot buffer. Disambiguates two cases that BOTH present as
//*   `positions == []` in the consumer but have opposite semantics:
//*
//*     1. Hydration race — tab just mounted, positionsRef hasn't populated yet
//*        AND nothing has streamed in on the trade_context topic. Default open
//*        so the very first band that arrives renders immediately instead of
//*        clipping at a phantom exit.
//*     2. Post-close — OMS published qty=0 which evicted positionsRef → the
//*        consumer sees positions=[]. The live buffer already has snapshots
//*        (the closed trade), so we KNOW we were streaming. Clip the band at
//*        the last snapshot (the exit bar) instead of letting TradeBandSeries
//*        project 2 bars past the actual exit candle.
//*
//*   Decision signal: presence of snapshots in the live buffer. Snapshots >0
//*   means we have been receiving — empty positions now means closed.
export const isPositionOpenForLiveBand = (positions, liveSnapshots) => {
    const pos = Array.isArray(positions) ? positions[0] : null;
    if (pos) {
        const qty = Number(pos.qty ?? pos.quantity ?? 0);
        return qty !== 0;
    }
    //--> No position record. Fall back to buffer presence.
    const bufferLen = Array.isArray(liveSnapshots) ? liveSnapshots.length : 0;
    return bufferLen === 0;
};

//--> issue-dual-sl-band-render (seam 1): ONE shared cross-source trade-identity key used by BOTH the
//*   merge dedup (buildCombinedLevelHistory) AND the open-trade WS-tail adoption
//*   (resolveOpenTradeSegment) so the two can never drift apart the way a bare entry_time did after
//*   #5's render-anchor migration (that drift IS this bug — issue #15 H1).
//*   Anchor = fill_bar_start when present (the bar-of-record, byte-stable across REST /open-trade + the
//*   WS buffer), ELSE entry_time — REST /trades (the closed `prior` lane) carries NO separate
//*   fill_bar_start; its entry_time is the same bar_start. 50-5: the actual fill instant is the
//*   separate `fill_time` field and is NEVER part of this identity key.
//! The `?? entry_time` fallback is LOAD-BEARING, not optional (fix-design D1). A bare `fill_bar_start`
//!  read yields a NULL key for every closed /trades element, `.filter(Boolean)` drops them all from
//!  priorKeys, and the dedup silently no-ops for the PRIMARY confirmed double-render (a closed REST
//!  trade vs its still-buffered WS twin, 72s signal-vs-fill bar gap — E4).
//! EXACT string equality only — NEVER a widened / range / tolerance match. Mirrors the OMS writer's
//!  own strict-equality rule + FLIP-widening regression history
//!  (order_level_writer._find_entry_order_by_fill_bar_start, order_level_writer.py:220-248).
//! Caution B completion (critic NOTE-2): two DISTINCT trades on the SAME fill bar + SAME side (a
//!  same-bar flatten-and-reenter — near-unreachable) collide on this key. That is harmless: seam 2
//!  dedups the REST `prior` array on entry_order_id (unique per order), NOT on this key, so both
//!  distinct closed trades survive; the key collision only affects the live-vs-prior Set (dropping a
//!  genuine same-trade duplicate — the desired behavior). Worst residual: a ≤250ms flicker before the
//!  openSeg's own entry_order_id resolves it. Do NOT "fix" the collision by deduping closed-vs-closed
//!  on this key — that would drop a legitimate distinct same-bar trade (pinned by a regression test).
//! R1.2.4 (#5-band multi-stage READ fix, money-path #2, iter-2 arch-critic C3): the #5-band's
//!  concurrent-multi-stage fix (order_levels.py, GET /order-levels/open-trade) means Caution B's
//!  "near-unreachable" no longer holds — two GENUINELY DISTINCT, genuinely-protected stage groups on the
//!  SAME account can now fill on the SAME candle with the SAME side (the exact collision Caution B
//!  describes), and unlike the closed-vs-closed case above, `buildCombinedLevelHistory` does NOT have an
//!  `exit_time != null` disjunct protecting two still-OPEN stage groups — both are `exit_time: null`.
//!  Fix: fold `entry_stage` (int, always present on `open_trades[i]`, server_default "1" for every
//!  single-stage row — order.py:303) into the key as a discriminator. `entry_stage ?? 1` means every
//!  single-stage row (100% of today's traffic, and every fixture/consumer that never sets `entry_stage`
//!  at all — closed /trades rows, the WS-buffer-derived `live` segments) computes the SAME numeric
//!  suffix it always would (both sides of any single-stage comparison default identically), so the
//!  COLLISION SHAPE for the single-stage case is byte-for-byte unchanged from before this fix — only a
//!  genuine `entry_stage > 1` group (which never existed pre-#5-band-fix) produces a new, distinguishing
//!  suffix. `identityKey` is module-private (never exported) — nothing outside this file depends on its
//!  literal string value, only on internal Set-membership/equality, so widening the string itself is
//!  safe as long as the RELATIVE collision behaviour for `entry_stage`-absent objects is preserved
//!  (it is, by the `?? 1` default). Known non-blocking gap: the WS `trade_context` payload
//!  (graph_evaluator._build_trade_context) does not carry `entry_stage` at all — a raw WS-buffer segment
//!  always defaults to `_stage1`, so `resolveOpenTradeSegment`'s WS-tail adoption for a genuine
//!  `entry_stage > 1` group degrades to the REST/synth base (never wrong data, just less-fresh) until the
//!  backend threads `entry_stage` onto the WS position snapshot — a backend fast-follow, out of scope here.
const identityKey = (t) => {
    const anchor = t.fill_bar_start ?? t.entry_time;             // fill-bar on all 3 surfaces
    if (!anchor) return null;
    const stage = t.entry_stage ?? 1;                            // R1.2.4: stage discriminator, defaults 1
    return `${anchor}_${String(t.side).toLowerCase()}_stage${stage}`;
};

//--> Combine prior closed trades (REST) with live snapshot segments (WS stream).
//*   Prior trades are already in chart shape; live ones are built from snapshots.
//*   Dedup on the COALESCED identityKey (fill_bar_start ?? entry_time, + side) — REST priorTrades win
//*   because they carry the authoritative exit_time / exit_price after the trade closes; the live
//*   buffer's last segment may still represent the same trade until the next entry resets the buffer.
//*   Without dedup the same just-closed trade renders twice (REST closed band + WS stale-projection
//*   band) at the same entry candle. Grouping (buildLiveLevelHistory) STAYS on entry_time; only what
//*   the MERGE reads is re-keyed.
export const buildCombinedLevelHistory = (priorTrades, liveSnapshots, { isPositionOpen = true } = {}) => {
    const prior = Array.isArray(priorTrades) ? priorTrades.filter((t) => t.level_history?.length) : [];
    const live = buildLiveLevelHistory(liveSnapshots, { isPositionOpen }) || [];

    if (process.env.NODE_ENV === 'development') {
        for (const trade of prior) {
            const hist = trade.level_history;
            for (let i = 1; i < hist.length; i++) {
                if (hist[i].timestamp < hist[i - 1].timestamp) {
                    // eslint-disable-next-line no-console
                    console.warn('[tradeLevelUtils] Non-monotonic timestamp in prior trade', trade.entry_order_id, i);
                }
            }
        }
    }

    //--> issue-dual-sl-band-render (seam 2): closed-wins self-dedup WITHIN the prior array. Over the OCO
    //*   window [TP-fill, SL-cancel] (~3-4s) an /open-trade "open segment" (exit_time null) and a /trades
    //*   "closed segment" (exit_time set) for the SAME trade both land in prior = [...priorTrades,
    //*   ...openSegs] (LiveAnalysisTab.jsx:164-167); the merge never dedups WITHIN prior. Drop the OPEN
    //*   seg when a CLOSED seg exists for the same trade — the closed row's exit_time/exit_price is
    //*   authoritative truth over an open segment's forward projection. Primary key entry_order_id
    //*   (present on BOTH /trades:260 AND openSegs:348 — a REST-vs-REST comparison, so the unique-per-order
    //*   id is valid here, unlike the WS twin in seam 1 which has none); fallback identityKey when
    //*   entry_order_id is absent on either side. Additive defense-in-depth — removes no existing gate.
    //! QA F1 correction (04-review.md): the entry_order_id-vs-identityKey key choice below governs ONLY
    //!  the OPEN-vs-CLOSED match above (does this OPEN dup belong to that CLOSED trade?) — proven
    //!  load-bearing by the "entry_order_id-drift" test in tradeLevelUtils.test.js (same order,
    //!  DISAGREEING fill_bar_start). It is NOT what protects two DISTINCT CLOSED trades sharing a key
    //!  (NOTE-2, below): those survive unconditionally because `t.exit_time != null` short-circuits
    //!  priorDeduped's filter (:298) before the key is ever consulted — true for entry_order_id or
    //!  identityKey alike. The prior wording of this note conflated the two; corrected here.
    //! NOTE-1 (fix-design D2 ruling): fires on RELOAD / OCO-fresh-mount ONLY. In Deepak's primary repro
    //!  (chart mounted THROUGH the close) refetchPriorTrades is dead (LiveAnalysisTab.jsx:106, never
    //!  invoked) so /trades never re-fetches the just-closed trade mid-session — seam 1 (fill_bar_start
    //!  rekey via the clamped WS-buffer twin) OWNS that repro. Wiring refetchPriorTrades is deliberately
    //!  OUT of #15 scope (grows blast radius on the #5-attested surface; #5 QA already ledgered it).
    const closedKeys = new Set(
        prior.filter((t) => t.exit_time != null)
             .map((t) => t.entry_order_id ?? identityKey(t))
             .filter(Boolean)
    );
    const priorDeduped = closedKeys.size === 0 ? prior : prior.filter(
        (t) => t.exit_time != null || !closedKeys.has(t.entry_order_id ?? identityKey(t))
    );

    //--> Build a key set from prior trades so live segments for the same entry are dropped.
    //    Include side in the key (via identityKey) to prevent over-dropping flip-exits.
    const priorKeys = new Set(priorDeduped.map(identityKey).filter(Boolean));
    const liveKept = live.filter((seg) => {
        const k = identityKey(seg);
        return k == null || !priorKeys.has(k);   // null key (pre-fill, no anchor) → always kept (Caution B)
    });

    const combined = [...priorDeduped, ...liveKept];
    combined.sort((a, b) => {
        const at = a.entry_time ? new Date(a.entry_time).getTime() : 0;
        const bt = b.entry_time ? new Date(b.entry_time).getTime() : 0;
        return at - bt;
    });
    return combined;
};

//--> issue-sltp-band-persistence: resolve ONE durable open-trade band segment for a single
//*   `open_trades[i]` account group (called once PER group by LiveAnalysisTab). Seeds the band from
//*   REST truth on mount/reload/reconnect, adopts the live WS tail ONLY when it identity-matches the
//*   REST entry (else discards the stale tail), synthesizes a rendered edge for a fresh zero-history
//*   fill, and lets the leg trigger_price win on drift at the CURRENT edge. buildCombinedLevelHistory
//*   (R7) stays UNCHANGED — the merge happens in the segment this feeds it.
//*
//*   openTrade  = one group element { entry_order_id, entry_time, fill_bar_start, side, entry_price,
//*                qty, current_levels:{stop_loss,take_profit}, level_history:[...] }
//*   Returns a chart-shape trade { entry_order_id, entry_time, fill_bar_start, exit_time:null, side,
//*   level_history } — or null when the group is falsy (contributes no band).
//--> issue 10509-6 (E13/D31) — pick the display-only level markers the OMS open-trade read derives:
//*   `leg_status`/`price_source` (a take-profit waiting for the exchange price band) and
//*   `lock_state` (a stop-loss at breakeven / locked). Absent keys stay absent (never fabricated).
//--> 50-17: the SL/TP the chart draws for each OPEN trade, by side, read off the SAME levelHistory the
//*   TradeLevelOverlay price lines use (latest known level on the open segment). Never a new fetch or a
//*   recomputed level. Returns { long, short }; a side with no open segment is null (no band drawn — NOT
//*   a flat verdict: position state comes from heldSideOf).
//--> 50-17: 'long' | 'short' | null from any side spelling (LONG/BUY, SHORT/SELL, any case).
const normSide = (v) => {
    const raw = String(v || '').toLowerCase();
    return raw === 'long' || raw === 'buy' ? 'long' : raw === 'short' || raw === 'sell' ? 'short' : null;
};

//--> 50-17: the side the position AUTHORITY says is held — the OMS position record (useTradingData
//*   `positions`, the same record isPositionOpenForLiveBand reads). qty 0 / side FLAT / no record (the OMS
//*   evicts a record at qty 0) -> null = flat. Never inferred from levels.
export const heldSideOf = (positions) => {
    const pos = Array.isArray(positions) ? positions[0] : null;
    if (!pos) return null;
    const qty = Number(pos.qty ?? pos.quantity ?? 0);
    if (!Number.isFinite(qty) || qty === 0) return null;
    return normSide(pos.side) ?? (qty > 0 ? 'long' : 'short');
};

//--> 50-17: the trade the chart's hover band shows at `time` (findTradeAtTime over buildTradeSpanMap —
//*   SmartChart's own hover lookup) and its SL/TP at that bar, with TradeLevelOverlay's fallback to the
//*   trade's first known level. null = no trade spans that bar.
export const levelsAtBar = (spanMap, time) => {
    const hit = time == null ? null : findTradeAtTime(spanMap, time);
    if (!hit) return null;
    const hist = Array.isArray(hit.trade.level_history) ? hit.trade.level_history : [];
    const at = (key) => {
        const v = hit.context?.levels?.[key]?.price ?? hist.find((e) => e?.levels?.[key]?.price != null)?.levels?.[key]?.price;
        return v != null && Number.isFinite(Number(v)) ? Number(v) : null;
    };
    return { side: normSide(hit.trade.side), stop: at('stop_loss'), target: at('take_profit') };
};

//--> 50-17: the trade segments the chart draws, reduced to what the dashboard's hovered-bar lookup reads
//*   (side, span, per-bar SL/TP prices) so it can ride a value-keyed prop (a raw levelHistory changes identity
//*   on every render and would defeat the memoised dashboard).
export const compactTradeLevels = (levelHistory) => (Array.isArray(levelHistory) ? levelHistory : [])
    .filter((t) => t && Array.isArray(t.level_history) && t.level_history.length > 0)
    .map((t) => ({
        side: t.side,
        entry_time: t.entry_time ?? t.entryTime ?? null,
        exit_time: t.exit_time ?? t.exitTime ?? null,
        level_history: t.level_history.map((e) => ({
            timestamp: e?.timestamp ?? null,
            levels: {
                stop_loss: { price: e?.levels?.stop_loss?.price ?? null },
                take_profit: { price: e?.levels?.take_profit?.price ?? null },
            },
        })),
    }));

export const openLevelsBySide = (levelHistory) => {
    const out = { long: null, short: null };
    for (const t of (Array.isArray(levelHistory) ? levelHistory : [])) {
        if (!t || t.exit_time != null) continue;
        const side = normSide(t.side);
        if (!side) continue;
        const hist = Array.isArray(t.level_history) ? t.level_history : [];
        const latest = (key) => {
            for (let i = hist.length - 1; i >= 0; i--) {
                const v = hist[i]?.levels?.[key]?.price;
                if (v != null && Number.isFinite(Number(v))) return Number(v);
            }
            return null;
        };
        out[side] = { stop: latest('stop_loss'), target: latest('take_profit') };
    }
    return out;
};

//--> 50-5-1: ONE predicate for "protection failed" (rejected by the broker OR never placed).
//*   The OMS wire values are REJECTED and NOT_PLACED; every FE consumer goes through this.
export const isFailedProtectionStatus = (s) => s === 'REJECTED' || s === 'NOT_PLACED';

function levelDisplayFields(edge) {
    const out = {};
    if (!edge) return out;
    //* Only these markers are carried (not the ordinary PENDING/leg_trigger provenance), so every
    //*  existing level point stays byte-identical. 50-5: a REJECTED leg also carries its reject_reason
    //*  (NOT_PLACED carries leg_status/price_source only).
    if (edge.leg_status === 'HELD_PRICE_BAND' || isFailedProtectionStatus(edge.leg_status)) {
        out.leg_status = edge.leg_status;
        if (edge.price_source != null) out.price_source = edge.price_source;
        if (edge.leg_status === 'REJECTED' && edge.reject_reason != null) out.reject_reason = edge.reject_reason;
    }
    if (edge.lock_state != null) out.lock_state = edge.lock_state;
    return out;
}

export const resolveOpenTradeSegment = (openTrade, liveSnapshots, { isPositionOpen = true } = {}) => {
    if (!openTrade) return null;                                     // step 5: falsy group → no band

    const ct = openTrade.current_levels || {};
    const restSide = String(openTrade.side || '').toLowerCase();

    //--> Step 1: derive the WS base via the SAME helper the merge uses (never a hand-rolled re-group)
    //*   so the emitted key matches buildCombinedLevelHistory's dedup key → dedups to one band.
    const wsSegs = buildLiveLevelHistory(liveSnapshots, { isPositionOpen });
    const wsOpen = wsSegs && wsSegs.length ? wsSegs[wsSegs.length - 1] : null;
    //--> Identity match (AC-3a): WS snapshot carries no OMS entry id — match on the SAME coalesced
    //*   identityKey the merge uses (fill_bar_start ?? entry_time, + side). issue-dual-sl-band-render
    //*   seam 1 / fix-design D1: the WS group's entry_time is the SIGNAL bar while REST /open-trade's is
    //*   the FILL bar (72s apart — E4), so a bare entry_time compare wrongly MISSES and discards the
    //*   fresher WS tail; both carry the SAME fill_bar_start, so the coalesced key matches. On MATCH
    //*   adopt the WS base (fresher, keeps appending) + adopt its entry_time/side as emitted keys.
    const wsMatched = !!(wsOpen
        && identityKey(wsOpen) != null
        && identityKey(wsOpen) === identityKey(openTrade));

    let base;
    let emitEntryTime;
    let emitSide;
    if (wsMatched) {
        base = Array.isArray(wsOpen.level_history) ? wsOpen.level_history.slice() : [];
        emitEntryTime = wsOpen.entry_time;
        emitSide = wsOpen.side;
    } else {
        //--> No WS buffer, OR a flip/reopen stale WS tail that MISMATCHES → REST/synth base, keys from
        //*   REST; discard the stale WS tail (adopting it would key REST's new data to the wrong entry).
        base = Array.isArray(openTrade.level_history) ? openTrade.level_history.slice() : [];
        emitEntryTime = openTrade.entry_time;
        emitSide = openTrade.side;
    }

    //--> Step 2: synthesis (D1 zero-rows). Fresh fill, no WS buffer AND zero persisted order_levels,
    //*   but ≥1 live edge → push ONE synthesized entry at fill_bar_start with the exact keys
    //*   toBandSegment reads (incl. a NON-NULL position so the point is never silently dropped).
    if (base.length === 0 && (ct.stop_loss || ct.take_profit)) {
        base.push({
            timestamp: openTrade.fill_bar_start,
            levels: {
                stop_loss: ct.stop_loss ? { price: ct.stop_loss.price, ...levelDisplayFields(ct.stop_loss) } : null,
                take_profit: ct.take_profit ? { price: ct.take_profit.price, ...levelDisplayFields(ct.take_profit) } : null,
                time_exit: null,
            },
            position: {
                entry_price: openTrade.entry_price,
                side: restSide,
                entry_time: openTrade.entry_time,
                qty: openTrade.qty,
            },
            order_status: null,
        });
    }

    //--> Step 3: legs-win-on-drift override at the CURRENT (last) point ONLY — prior bars untouched.
    if (base.length > 0) {
        const li = base.length - 1;
        const prevLevels = base[li].levels || {};
        let stop_loss;
        let take_profit;
        if (wsMatched) {
            //--> WS base present → leg is a FLOOR: keep a non-null WS value (the intended live display),
            //*   fill only where the WS value is null. Avoids reverting a just-trailed value.
            const wsSL = prevLevels.stop_loss;
            const wsTP = prevLevels.take_profit;
            //--> 50-5 R3-T2: a leg the OMS REJECTED / NOT_PLACED is never overridden by the WS intent price.
            stop_loss = isFailedProtectionStatus(ct.stop_loss?.leg_status)
                ? { ...(wsSL || {}), price: ct.stop_loss.price }
                : (wsSL && wsSL.price != null)
                ? wsSL
                : (ct.stop_loss ? { price: ct.stop_loss.price } : (wsSL ?? null));
            take_profit = isFailedProtectionStatus(ct.take_profit?.leg_status)
                ? { ...(wsTP || {}), price: ct.take_profit.price }
                : (wsTP && wsTP.price != null)
                ? wsTP
                : (ct.take_profit ? { price: ct.take_profit.price } : (wsTP ?? null));
        } else {
            //--> REST/synth base → leg WINS (full override): the persisted last row is proven stale.
            //*   A null leg side → null (honest one-sided / no-live edge; renders nothing, never 0).
            stop_loss = ct.stop_loss ? { ...(prevLevels.stop_loss || {}), price: ct.stop_loss.price } : null;
            take_profit = ct.take_profit ? { ...(prevLevels.take_profit || {}), price: ct.take_profit.price } : null;
        }
        //--> Copy the last point (+ its levels) before overriding so the source array/objects (React
        //*   state / WS buffer) are never mutated in place.
        //--> issue 10509-6 (E13/D31) — the display-only markers (held-TP leg_status / SL lock_state)
        //*   come ONLY from the REST legs (`current_levels`); carry them onto whichever price won.
        if (stop_loss && ct.stop_loss) stop_loss = { ...stop_loss, ...levelDisplayFields(ct.stop_loss) };
        if (take_profit && ct.take_profit) take_profit = { ...take_profit, ...levelDisplayFields(ct.take_profit) };
        base[li] = { ...base[li], levels: { ...prevLevels, stop_loss, take_profit } };
    }

    //--> Step 4: emit. entry_time/side are the step-1 coalesce-adopted keys; fill_bar_start + exit_time
    //*   null are load-bearing (isLivePreFill guard + open-projection).
    //!   R1.2.4: thread `entry_stage` through unchanged from the REST group (`openTrade.entry_stage`,
    //!    always present, server_default 1) so this emitted segment's own `identityKey` computation
    //!    (both here — via a caller re-invoking identityKey on the returned object — and in
    //!    buildCombinedLevelHistory once this segment lands in `priorPlus`) matches the SAME group's
    //!    REST truth rather than silently defaulting to `_stage1` and colliding with a sibling stage.
    return {
        entry_order_id: openTrade.entry_order_id,
        entry_time: emitEntryTime,
        fill_bar_start: openTrade.fill_bar_start,
        fill_time: openTrade.fill_time,
        exit_time: null,
        side: emitSide,
        entry_price: openTrade.entry_price,
        entry_stage: openTrade.entry_stage,
        level_history: base,
    };
};
